import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarOff, Contact, Timer, Rocket, Download, Smartphone } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { useCurrentUser } from '@/lib/auth';
import { useMyEmployee } from '@/lib/employees';
import { useTodayAttendance, useMyAttendance, useClockIn, useClockOut } from '@/lib/attendance';
import { useInstallPrompt } from '@/lib/pwa';

function hhmm(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Mobile-first employee hub — the PWA's start_url. Big tap targets for the
 * handful of things a non-desk worker does from a phone: punch in/out,
 * request leave, check their record, complete onboarding. Wrapped in
 * AppShell so it's also reachable from the desktop sidebar.
 */
export function MePage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const me = useMyEmployee();
  const today = useTodayAttendance();
  const now = new Date();
  const month = useMyAttendance(now.getFullYear(), now.getMonth() + 1);
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const install = useInstallPrompt();
  const [breakMin, setBreakMin] = useState('0');

  const rec = today.data?.record;
  const isIn = !!rec?.clockInAt && !rec?.clockOutAt;
  const isDone = !!rec?.clockInAt && !!rec?.clockOutAt;
  const firstName = (user.data?.displayName || '').split(' ')[0] || t('me.there', { defaultValue: 'there' });
  const sum = month.data?.summary;

  return (
    <AppShell>
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-2xl mx-auto">
        <header>
          <h1 className="display text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900 serif-jp">
            {t('me.greeting', { name: firstName, defaultValue: 'Hi, {{name}}' })}
          </h1>
          <p className="mt-1 text-sm text-ink-500">{t('me.subtitle', { defaultValue: 'Your space — clock in, request time off, and manage your details.' })}</p>
        </header>

        {/* Install-as-app prompt (Chrome/Edge/Android). iOS shows a manual hint. */}
        {install.canInstall && !install.standalone && (
          <button
            onClick={() => install.promptInstall()}
            className="mt-5 w-full flex items-center gap-3 rounded-2xl border border-accent-500/30 bg-accent-50 px-4 py-3 text-start hover:bg-accent-100 transition"
          >
            <Download className="h-5 w-5 text-accent-600 shrink-0" />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-accent-800">{t('me.install_title', { defaultValue: 'Install KeenEZ' })}</span>
              <span className="block text-xs text-accent-700/80">{t('me.install_hint', { defaultValue: 'Add to your home screen for one-tap clock-in.' })}</span>
            </span>
          </button>
        )}

        {/* Today clock — the primary action. */}
        <section id="clock" className="mt-5 rounded-2xl border border-ink-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{today.data?.date ?? '—'}</p>
              <p className="mt-0.5 text-xl font-bold text-ink-900">
                {isIn ? t('me.clockedIn', { defaultValue: 'Clocked in' }) : isDone ? t('me.doneToday', { defaultValue: 'Done for today' }) : t('me.notClockedIn', { defaultValue: 'Not clocked in' })}
              </p>
              <p className="mt-0.5 text-xs text-ink-600">
                {t('me.inOut', { in: hhmm(rec?.clockInAt ?? null), out: hhmm(rec?.clockOutAt ?? null), defaultValue: 'In {{in}} · Out {{out}}' })}
              </p>
            </div>
            {isDone && <span className="text-sm text-emerald-600 font-semibold shrink-0">✓</span>}
          </div>

          {!isIn && !isDone && (
            <button
              onClick={() => clockIn.mutate(undefined)}
              disabled={clockIn.isPending}
              className="mt-4 w-full inline-flex h-14 items-center justify-center rounded-2xl bg-accent-500 text-white text-base font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
            >
              {clockIn.isPending ? t('me.clockingIn', { defaultValue: 'Clocking in…' }) : `🟢 ${t('me.clockIn', { defaultValue: 'Clock in' })}`}
            </button>
          )}
          {isIn && (
            <div className="mt-4 flex items-end gap-3">
              <label className="text-xs text-ink-600">
                <span className="block mb-1">{t('me.break', { defaultValue: 'Break (min)' })}</span>
                <input type="number" min={0} inputMode="numeric" value={breakMin} onChange={(e) => setBreakMin(e.target.value)}
                  className="w-24 border border-ink-300 rounded-xl px-3 py-3 text-base focus:border-accent-500 focus:outline-none" />
              </label>
              <button
                onClick={() => clockOut.mutate(Number(breakMin) || 0)}
                disabled={clockOut.isPending}
                className="flex-1 inline-flex h-14 items-center justify-center rounded-2xl bg-ink-900 text-white text-base font-semibold hover:bg-ink-800 disabled:opacity-50 transition"
              >
                {clockOut.isPending ? t('me.clockingOut', { defaultValue: 'Clocking out…' }) : `🔴 ${t('me.clockOut', { defaultValue: 'Clock out' })}`}
              </button>
            </div>
          )}

          {sum && (
            <div className="mt-4 grid grid-cols-3 gap-2 border-t border-ink-100 pt-3 text-center">
              <MiniStat label={t('me.mDays', { defaultValue: 'Days' })} value={String(sum.daysWorked)} />
              <MiniStat label={t('me.mHours', { defaultValue: 'Hours' })} value={String(sum.totalWorkedHours)} />
              <MiniStat label={t('me.mOt', { defaultValue: 'OT h' })} value={String(sum.overtimeHours)} warn={sum.violations.length > 0} />
            </div>
          )}
        </section>

        {/* Quick actions. */}
        <section className="mt-5 grid grid-cols-2 gap-3">
          <ActionCard to="/app/time-off" Icon={CalendarOff} title={t('me.timeOff', { defaultValue: 'Request time off' })} sub={t('me.timeOffSub', { defaultValue: '有給 · leave' })} />
          <ActionCard to="/app/attendance" Icon={Timer} title={t('me.attendance', { defaultValue: 'Time clock' })} sub={t('me.attendanceSub', { defaultValue: 'History' })} />
          <ActionCard to="/app/profile" Icon={Contact} title={t('me.record', { defaultValue: 'My record' })} sub={me.data ? (me.data.legalNameLocal ?? '') : t('me.recordSub', { defaultValue: 'Details & changes' })} />
          <ActionCard to="/onboarding" Icon={Rocket} title={t('me.onboarding', { defaultValue: 'Onboarding' })} sub={t('me.onboardingSub', { defaultValue: 'Get set up' })} />
        </section>

        {/* iOS manual install hint (Safari never fires beforeinstallprompt). */}
        {!install.canInstall && !install.standalone && (
          <p className="mt-5 flex items-center gap-2 text-[11px] text-ink-400">
            <Smartphone className="h-3.5 w-3.5" />
            {t('me.iosHint', { defaultValue: 'On iPhone: tap Share → “Add to Home Screen” to install.' })}
          </p>
        )}
      </div>
    </AppShell>
  );
}

function MiniStat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <p className={`num text-lg font-bold ${warn ? 'text-warn-600' : 'text-ink-900'}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</p>
    </div>
  );
}

function ActionCard({ to, Icon, title, sub }: { to: string; Icon: React.ComponentType<{ className?: string }>; title: string; sub: string }) {
  return (
    <Link
      to={to}
      className="rounded-2xl border border-ink-200 bg-white p-4 flex flex-col gap-2 active:scale-[0.98] hover:border-accent-300 hover:shadow-sm transition min-h-[104px]"
    >
      <Icon className="h-6 w-6 text-accent-600" />
      <div className="mt-auto">
        <p className="text-sm font-semibold text-ink-900 leading-tight">{title}</p>
        {sub && <p className="text-[11px] text-ink-500 truncate">{sub}</p>}
      </div>
    </Link>
  );
}
